/**
 * Rich project-context card for Nostr people, articles, and events.
 * Larger than composer chips: banner, avatar, about/snippet, pack badge.
 */

import { useEffect, useState, type ReactNode } from 'react';
import {
  AtSign,
  BadgeCheck,
  BookOpen,
  FileText,
  Loader2,
  MessagesSquare,
  Pin,
  Radio,
  RefreshCw,
  Trash2,
  User,
} from 'lucide-react';
import { useNostr } from '@nostrify/react';

import type {
  ContextItem,
  NostrContextMeta,
  NostrContextPack,
} from '@/lib/workspace/types';
import { isContextCacheStale } from '@/lib/workspace/types';
import { enrichChipToMeta, packLabel } from '@/lib/nostr/enrichContext';
import { cn } from '@/lib/utils';

function shortNpub(ref: string): string {
  const s = ref.replace(/\s+/g, '');
  if (s.length <= 20) return s;
  return `${s.slice(0, 12)}…${s.slice(-8)}`;
}

/** Pack recipes the user can cycle between without remove/re-add. */
function packOptionsFor(meta?: NostrContextMeta): NostrContextPack[] {
  if (!meta?.pack) return [];
  switch (meta.pack) {
    case 'my-profile':
    case 'my-recent':
      return ['my-profile', 'my-recent'];
    case 'profile-brief':
    case 'profile-recent':
      return ['profile-brief', 'profile-recent'];
    case 'article':
      return ['article', 'thread'];
    case 'thread':
      return meta.entity === 'addr' ? ['article', 'thread'] : ['single', 'thread'];
    case 'single':
      return ['single', 'thread'];
    default:
      return [];
  }
}

/** Compact "3d ago" style relative time. */
function relAge(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function entityIcon(meta?: NostrContextMeta) {
  const pack = meta?.pack;
  if (pack === 'network-search' || meta?.entity === 'pack') {
    return <Radio className="w-3 h-3" />;
  }
  if (pack === 'article' || (meta?.entity === 'addr' && meta.eventKind === 30023)) {
    return <BookOpen className="w-3 h-3" />;
  }
  if (pack === 'thread' || meta?.entity === 'thread') {
    return <MessagesSquare className="w-3 h-3" />;
  }
  if (
    pack === 'my-profile' ||
    pack === 'my-recent' ||
    pack === 'profile-brief' ||
    pack === 'profile-recent' ||
    meta?.entity === 'profile'
  ) {
    return <User className="w-3 h-3" />;
  }
  return <FileText className="w-3 h-3" />;
}

export function ProjectNostrContextCard({
  item,
  onUpdate,
  onRemove,
  onTogglePin,
}: {
  item: ContextItem;
  onUpdate: (patch: Partial<ContextItem>) => void;
  onRemove: () => void;
  onTogglePin: () => void;
}) {
  const { nostr } = useNostr();
  const [enriching, setEnriching] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);

  const meta = item.meta;
  const title = item.title || meta?.displayName || 'Nostr';
  const banner = meta?.image;
  const avatar = meta?.picture;
  const nip05 = meta?.nip05;
  const about = meta?.about || meta?.snippet;
  const pack = packLabel(meta?.pack, meta?.entity);
  const needsEnrich =
    (!meta?.picture &&
      !meta?.image &&
      !meta?.about &&
      !meta?.snippet &&
      !meta?.cachedText) ||
    // Stale live packs (recent notes) quietly re-fetch when the card is viewed
    isContextCacheStale(meta);

  // Auto-enrich once when card mounts with thin meta
  useEffect(() => {
    if (!needsEnrich || enriching) return;
    let cancelled = false;
    setEnriching(true);
    void (async () => {
      try {
        const chip = {
          id: item.id,
          title: item.title || item.content,
          ref: meta?.ref || item.content,
          pack: meta?.pack || ('single' as const),
          pubkey: meta?.authorPubkey,
          nip05: meta?.nip05,
          picture: meta?.picture,
          image: meta?.image,
          snippet: meta?.snippet,
          // Stale snapshots must not short-circuit the re-fetch
          cachedText: isContextCacheStale(meta) ? undefined : meta?.cachedText,
          noteLimit: meta?.noteLimit,
        };
        const next = await enrichChipToMeta(chip, (f, o) => nostr.query(f, o));
        if (cancelled) return;
        onUpdate({
          title: item.title || next.displayName || item.title,
          meta: { ...meta, ...next, ref: next.ref || meta?.ref || item.content },
        });
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setEnriching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Only on mount / when item id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  const refresh = (overridePack?: NostrContextPack) => {
    setEnriching(true);
    setImgFailed(false);
    setAvatarFailed(false);
    void (async () => {
      try {
        const chip = {
          id: item.id,
          title: item.title || item.content,
          ref: meta?.ref || item.content,
          pack: overridePack || meta?.pack || ('single' as const),
          pubkey: meta?.authorPubkey,
          nip05: meta?.nip05,
          noteLimit: meta?.noteLimit,
        };
        const next = await enrichChipToMeta(chip, (f, o) => nostr.query(f, o));
        onUpdate({
          title: next.displayName || item.title,
          meta: {
            ...meta,
            pack: overridePack ?? meta?.pack,
            ...next,
            ref: next.ref || meta?.ref || item.content,
          },
        });
      } finally {
        setEnriching(false);
      }
    })();
  };

  // Cycle pack recipe (profile ↔ notes, event ↔ thread, …) without re-adding
  const packOptions = packOptionsFor(meta);
  const cyclePack = () => {
    if (packOptions.length < 2 || enriching) return;
    const idx = packOptions.indexOf(meta?.pack as NostrContextPack);
    const next = packOptions[(idx + 1) % packOptions.length]!;
    // Clear the old snapshot first so prompts never reuse the wrong pack body
    onUpdate({
      meta: {
        ...meta,
        ref: meta?.ref || item.content,
        entity: meta?.entity || 'event',
        pack: next,
        cachedText: undefined,
        cachedAt: undefined,
      } as NostrContextMeta,
    });
    refresh(next);
  };

  const isArticle =
    meta?.pack === 'article' ||
    (meta?.entity === 'addr' && meta.eventKind === 30023);
  const isProfile =
    meta?.entity === 'profile' ||
    meta?.pack === 'my-profile' ||
    meta?.pack === 'my-recent' ||
    meta?.pack === 'profile-brief' ||
    meta?.pack === 'profile-recent';

  const hasBanner = Boolean(banner && !imgFailed);
  const hasHeaderStrip = hasBanner || isArticle || isProfile;

  return (
    <div className="group relative rounded-xl border border-[#1f1f1f] bg-[#0f0f0f] hover:border-[#2a2a2a] transition-colors">
      {/*
        Header: banner clipped to top radius only.
        Avatar sits in the body with -mt and z-10 so it always paints above the banner
        (positioned banner children were stacking over the in-flow avatar).
      */}
      {hasBanner ? (
        <div className="relative h-[4.5rem] w-full overflow-hidden rounded-t-xl bg-[#141414] isolate">
          <img
            src={banner}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setImgFailed(true)}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f0f] via-[#0f0f0f]/50 to-transparent pointer-events-none" />
        </div>
      ) : isArticle || isProfile ? (
        <div
          className={cn(
            'h-11 w-full rounded-t-xl',
            isArticle
              ? 'bg-gradient-to-br from-[#1a1520] via-[#141414] to-[#0f0f0f]'
              : 'bg-gradient-to-br from-[#151a20] via-[#141414] to-[#0f0f0f]',
          )}
        />
      ) : null}

      <div className={cn('relative z-10 px-3 pb-3', hasHeaderStrip ? 'pt-0' : 'pt-3')}>
        <div className={cn('flex items-end gap-2.5', hasHeaderStrip && '-mt-5')}>
          {/* Avatar — ring clears banner; z-20 wins stacking */}
          <div
            className={cn(
              'relative z-20 shrink-0 rounded-full bg-[#1a1a1a] overflow-hidden flex items-center justify-center shadow-[0_0_0_3px_#0f0f0f]',
              hasHeaderStrip ? 'w-12 h-12' : 'w-10 h-10',
            )}
          >
            {avatar && !avatarFailed ? (
              <img
                src={avatar}
                alt=""
                className="w-full h-full object-cover"
                onError={() => setAvatarFailed(true)}
              />
            ) : (
              <span className="text-[#5a5a5a]">{entityIcon(meta)}</span>
            )}
          </div>

          <div className={cn('min-w-0 flex-1', hasHeaderStrip ? 'pb-0.5 pt-5' : 'pt-0.5')}>
            <div className="flex items-start justify-between gap-1">
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-[#e8e6e3] truncate leading-tight">
                  {title}
                </div>
                <div className="text-[10px] text-[#5a5a5a] truncate mt-0.5">
                  {nip05 ? (
                    <span className="inline-flex items-center gap-1 min-w-0 max-w-full">
                      <span className="truncate text-[#8a8884]">{nip05}</span>
                      {meta?.nip05Verified === true && (
                        <BadgeCheck
                          className="w-3 h-3 shrink-0 text-sky-400/80"
                          aria-label="NIP-05 verified"
                        />
                      )}
                      {meta?.nip05Verified === false && (
                        <span className="shrink-0 text-amber-500/70">unverified</span>
                      )}
                    </span>
                  ) : (
                    <span className="font-mono">{shortNpub(meta?.ref || item.content)}</span>
                  )}
                </div>
              </div>
              <div
                className={cn(
                  'flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity',
                  hasHeaderStrip && 'pt-5',
                )}
              >
                <button
                  type="button"
                  onClick={() => void refresh()}
                  disabled={enriching}
                  className="p-1 rounded text-[#3a3a3a] hover:text-[#e8e6e3] transition-colors disabled:opacity-40"
                  title="Refresh from relays"
                >
                  {enriching ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3 h-3" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={onTogglePin}
                  className={cn(
                    'p-1 rounded transition-colors',
                    item.pinned ? 'text-[#e8e6e3]' : 'text-[#3a3a3a] hover:text-[#5a5a5a]',
                  )}
                  title="Pin — pinned items ship first when the context budget runs out"
                >
                  <Pin className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  onClick={onRemove}
                  className="p-1 rounded text-[#3a3a3a] hover:text-red-400 transition-colors"
                  title="Remove"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Badges */}
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {packOptions.length > 1 ? (
            <button
              type="button"
              onClick={cyclePack}
              disabled={enriching}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[#141414] border border-[#1f1f1f] text-[9px] uppercase tracking-wider text-[#5a5a5a] hover:border-[#3a3a3a] hover:text-[#8a8884] transition-colors disabled:opacity-50"
              title="Switch pack type — re-fetches from relays"
            >
              <AtSign className="w-2.5 h-2.5" />
              {pack}
              {item.pinned ? ' · pinned' : ''}
              <RefreshCw className="w-2 h-2 text-[#3a3a3a]" />
            </button>
          ) : (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[#141414] border border-[#1f1f1f] text-[9px] uppercase tracking-wider text-[#5a5a5a]">
              <AtSign className="w-2.5 h-2.5" />
              {pack}
              {item.pinned ? ' · pinned' : ''}
            </span>
          )}
          {meta?.eventKind != null && meta.entity !== 'profile' && (
            <span className="text-[9px] text-[#3a3a3a] tabular-nums">
              kind {meta.eventKind}
            </span>
          )}
          {meta?.cachedAt && meta.entity !== 'pack' && (
            <span
              className={cn(
                'text-[9px] tabular-nums',
                isContextCacheStale(meta) ? 'text-amber-500/70' : 'text-[#3a3a3a]',
              )}
              title={
                isContextCacheStale(meta)
                  ? 'Snapshot is older than the freshness window — it re-fetches live on your next send'
                  : 'Last relay fetch'
              }
            >
              {isContextCacheStale(meta)
                ? 'stale · refreshes at send'
                : `updated ${relAge(meta.cachedAt)}`}
            </span>
          )}
          {meta?.entity === 'pack' && (
            <span className="text-[9px] text-[#3a3a3a]">live at send</span>
          )}
          {enriching && !meta?.picture && (
            <span className="inline-flex items-center gap-1 text-[9px] text-[#3a3a3a]">
              <Loader2 className="w-2.5 h-2.5 animate-spin" />
              fetching…
            </span>
          )}
        </div>

        {/* About / snippet */}
        {about && (
          <p className="mt-2 text-[11px] text-[#8a8884] leading-relaxed line-clamp-3">
            {about}
          </p>
        )}

        {/* Editable title (subtle) */}
        <input
          value={item.title ?? ''}
          onChange={(e) => onUpdate({ title: e.target.value })}
          placeholder="Title"
          className="mt-2 w-full bg-transparent text-[11px] text-[#3a3a3a] placeholder:text-[#2a2a2a] focus:outline-none focus:text-[#5a5a5a] border-t border-transparent focus:border-[#1f1f1f] pt-1.5"
          title="Edit display title"
        />
      </div>
    </div>
  );
}

/** Compact note/url cards stay simple */
export function ProjectPlainContextCard({
  item,
  typeIcon,
  onUpdate,
  onRemove,
  onTogglePin,
}: {
  item: ContextItem;
  typeIcon: ReactNode;
  onUpdate: (patch: Partial<ContextItem>) => void;
  onRemove: () => void;
  onTogglePin: () => void;
}) {
  return (
    <div className="group rounded-xl border border-[#1f1f1f] bg-[#0f0f0f] p-3 space-y-1.5 hover:border-[#2a2a2a] transition-colors">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#3a3a3a]">
          {typeIcon}
          {item.type}
          {item.pinned ? ' · pinned' : ''}
        </span>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={onTogglePin}
            className={cn(
              'p-1 rounded transition-colors',
              item.pinned ? 'text-[#e8e6e3]' : 'text-[#3a3a3a] hover:text-[#5a5a5a]',
            )}
            title="Pin — pinned items ship first when the context budget runs out"
          >
            <Pin className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="p-1 rounded text-[#3a3a3a] hover:text-red-400 transition-colors"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
      <input
        value={item.title ?? ''}
        onChange={(e) => onUpdate({ title: e.target.value })}
        placeholder="Title"
        className="w-full bg-transparent text-[13px] text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none"
      />
      <textarea
        value={item.content}
        onChange={(e) => onUpdate({ content: e.target.value })}
        rows={2}
        className="w-full bg-transparent text-[12px] text-[#5a5a5a] focus:outline-none resize-y font-mono leading-relaxed"
      />
    </div>
  );
}
